import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BigNumber from 'bignumber.js';
import { NavLink } from 'react-router-dom';
import {
  currencyFormat,
  numberFormat,
  vBigNumber,
  vGreaterThanBigNumber,
  vMaxBigNumber,
  vRequired,
  currentLocale,
  datetimeFormat,
} from '@tokenyico/design-system';
import {
  DsButton,
  DsCard,
  DsForm,
  DsIcon,
  DsInputBignumber,
  DsSelect,
  // DsInputCheckbox,
  DsInputString,
  DsLoader,
  DsTag,
  DsSelectOption,
  DsLogo,
  DsTooltip,
} from '@tokenyico/design-system/dist/reactJS';
import { dateFormat } from '@tokenyico/design-system';

import { RedemptionService } from 'services';

import './Redemption.less';
import { logoAssets } from '@tokenyico/design-system/dist/collection/components/ds-logo/types';
import { connect } from 'redux-zero/react';
import { formActions } from 'app/state/actions';

const presentCurrency = (amount, currency, decimals = 2) => {
  try {
    return currencyFormat(
      typeof amount === 'string' ? parseFloat(amount) : amount.toNumber(),
      currency,
      true,
    );
  } catch (e) {} // Just in case currency is not recognised, use token style presentation
  return presentToken(amount, decimals, currency);
};

const symbolBefore = currencyFormat(0, 'EUR').indexOf('0') !== 0;
const presentToken = (amount, decimals, symbol) => {
  const num = numberFormat(amount, decimals);
  if (symbolBefore)
    return (
      <span>
        <span className="_font-regular" style={{ fontSize: '0.9em' }}>
          {symbol}
        </span>{' '}
        {num}
      </span>
    );
  else
    return (
      <span>
        {num}{' '}
        <span className="_font-regular" style={{ fontSize: '0.9em' }}>
          {symbol}
        </span>
      </span>
    );
};

const currencySymbol = (currency) => {
  const parts = new Intl.NumberFormat(currentLocale(), {
    style: 'currency',
    currency,
  }).formatToParts(0);
  return parts.find((part) => part.type === 'currency')?.value ?? currency;
};

const RedemptionView = ({ redemption, token, balance, setError, lastValuationDate }) => {
  const valuesInitialState = {
    tokenAmount: null,
    paymentCurrency: redemption.paymentCurrencies[0],
    paymentInformation: {
      bankName: '',
      accountOwner: '',
      bicSwift: '',
      iban: '',
    },
  };
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [values, setValues] = useState(valuesInitialState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOrderCreated, setIsOrderCreated] = useState(false);
  const [exchangeRates, setExchangeRates] = useState(null);
  const [exchangeRatio, setExchangeRatio] = useState(null);
  let [tokenPrice, setTokenPrice] = useState();
  const [hasTokenPrice, setHasTokenPrice] = useState(true);

  useEffect(() => {
    setTokenPrice(
      (tokenPrice =
        redemption.type === 'OPEN_ENDED'
          ? token.lastValuationPrice?.valuation ?? 0
          : redemption.tokenPrice),
    );
    if (parseInt(tokenPrice) === 0) {
      setHasTokenPrice(false);
    } else {
      setHasTokenPrice(true);
    }

    (async () => {
      const exchangeRates = await RedemptionService.getExchangeRates(redemption.redemptionId);
      setExchangeRates(exchangeRates);
      setIsLoading(false);
    })();
  }, [redemption.redemptionId]);

  useEffect(() => {
    if (!exchangeRates) return;
    setExchangeRatio(
      exchangeRates.find((e) => e.quoteCurrency === values.paymentCurrency).exchangeRate,
    );
  }, [values, exchangeRates]);

  const onSubmit = async () => {
    setIsSubmitting(true);
    try {
      await RedemptionService.createRedemptionOrder({
        redemptionId: redemption.redemptionId,
        ...values,
      });
      setIsOrderCreated(true);
    } catch (e) {
      setError(e);
    }
    setIsSubmitting(false);
  };

  const renderCalendar = () => {
    return (
      <div className="calendar">
        <div className="row">
          <p className="_text-coolGrey-500">
            {t('shared.cutOffDateTime')}
            <DsTooltip isVisibleOnHover text={t('shared.cutOffTooltip')} className="_mleft-2">
              <DsIcon name="info" size="0.8rem" color="var(--coolGrey-400)" />
            </DsTooltip>
          </p>
          <p className="_text-coolGrey-700">
            {datetimeFormat(new Date(Date.parse(redemption.calendar.cutOff)))}
          </p>
        </div>
        <div className="row">
          <p className="_text-coolGrey-500">
            {t('shared.tradeDate')}
            <DsTooltip isVisibleOnHover text={t('shared.tradeDateTooltip')} className="_mleft-2">
              <DsIcon name="info" size="0.8rem" color="var(--coolGrey-400)" />
            </DsTooltip>
          </p>
          <p className="_text-coolGrey-700">
            {dateFormat(new Date(Date.parse(redemption.calendar.tradeDate)), true)}
          </p>
        </div>
      </div>
    );
  };

  if (isLoading)
    return (
      <div id="redemption-view">
        <DsLoader version="dots" color="var(--primary)" />
      </div>
    );

  return (
    <div id={isOrderCreated ? 'order-created-view' : 'redemption-view'}>
      <div className="side-bar">
        <header>
          <div className="logo">
            <img src={token.image} alt="*" />
          </div>
          <div className="name">
            <div className="h3">{token.symbol}</div>
            <div className="_text-sm _text-coolGrey-500 _mtop-1">{token.name}</div>
          </div>
        </header>
      </div>
      <div className="container">
        {isOrderCreated ? (
          <>
            <h3>{t('shared.redemptionOrderCreated')}</h3>
            <p className="_text-coolGrey-400">{t('shared.redemptionOrderCreatedHelperText')}</p>
          </>
        ) : (
          <>
            <h3>{t('shared.redeemTokens')}</h3>
            <p className="_text-coolGrey-400">{t('shared.redeemTokenHelperText')}</p>
          </>
        )}
        {isOrderCreated ? (
          <>
            <div className="summary-content">
              <p className="title">{t('shared.redemptionOrderSummary')}</p>
              {redemption.calendar?.cutOff && redemption.calendar?.tradeDate ? (
                renderCalendar()
              ) : (
                <> </>
              )}
              <div className="recap _mbottom-0">
                <div className="row">
                  <p className="_text-coolGrey-400">{t('shared.status')}</p>
                  <div className="status">
                    <DsTag label="Initiated" version="warning" icon="pending" size="xs" />
                  </div>
                </div>
                <div className="row border-top _ptop-3">
                  <p className="_text-coolGrey-400">{t('shared.tokensToRedeem')}</p>
                  <p>{presentToken(values.tokenAmount ?? 0, token.decimals, token.symbol)}</p>
                </div>
                <div className="row">
                  <p className="_text-coolGrey-400">
                    {redemption.type === 'OPEN_ENDED'
                      ? t('shared.estimatedPricePerToken')
                      : t('shared.pricePerToken')}{' '}
                  </p>
                  <p>
                    {hasTokenPrice ? (
                      presentCurrency(
                        new BigNumber(tokenPrice)
                          .multipliedBy(exchangeRatio)
                          .decimalPlaces(2, BigNumber.ROUND_DOWN),
                        values.paymentCurrency,
                      )
                    ) : (
                      <DsTooltip
                        isVisibleOnHover={true}
                        text={t('shared.amountReceivedSettledByIssuer')}
                      >
                        <DsIcon name="info" size="0.8rem" color="var(--coolGrey-400)" />
                      </DsTooltip>
                    )}
                  </p>
                </div>
                <hr className="_mbottom-2" />
                <div className="row">
                  <p className="_text-coolGrey-400">
                    {redemption.type === 'OPEN_ENDED'
                      ? t('shared.estimatedAmountToReceive')
                      : t('shared.amountToReceive')}{' '}
                  </p>
                  <p>
                    {hasTokenPrice ? (
                      presentCurrency(
                        new BigNumber(values.tokenAmount ?? 0)
                          .multipliedBy(tokenPrice)
                          .multipliedBy(exchangeRatio)
                          .decimalPlaces(2, BigNumber.ROUND_DOWN),
                        values.paymentCurrency,
                      )
                    ) : (
                      <DsTooltip
                        isVisibleOnHover={true}
                        text={t('shared.amountReceivedSettledByIssuer')}
                      >
                        <DsIcon name="info" size="0.8rem" color="var(--coolGrey-400)" />
                      </DsTooltip>
                    )}
                  </p>
                </div>
              </div>
            </div>
            <NavLink exact to="/orders/redemptions">
              <DsButton
                type="button"
                label={t('shared.goToOrders')}
                version="primary"
                icon="arrow-right"
              />
            </NavLink>
          </>
        ) : (
          <div className="content">
            <DsForm onSubmit={onSubmit}>
              <div className="form-content">
                <div className="redemption-form">
                  <div className="first-section">
                    <DsInputBignumber
                      name="tokenAmount"
                      label={t('shared.tokensToRedeem') + ' *'}
                      symbol={token.symbol}
                      value={values.tokenAmount}
                      onDsInput={({ detail }) => {
                        setValues({ ...values, tokenAmount: detail });
                      }}
                      validate={(v) =>
                        vRequired(v) ||
                        vBigNumber(token.decimals)(v) ||
                        vGreaterThanBigNumber(0)(v) ||
                        vMaxBigNumber(balance?.unfrozenBalance ?? 0)(v)
                      }
                    />
                    <div className="token-balance">
                      <p className="balance">
                        {t('shared.balance')}:{' '}
                        {presentToken(balance?.unfrozenBalance, token.decimals, token.symbol)}
                      </p>
                      <p
                        className="set-max"
                        onClick={() =>
                          setValues({ ...values, tokenAmount: balance.unfrozenBalance })
                        }
                      >
                        {t('shared.max')}
                      </p>
                    </div>
                    {redemption.paymentCurrencies.length > 1 ? (
                      <DsSelect
                        name="paymentCurrency"
                        label={t('shared.redemptionPaymentCurrency')}
                        value={values.paymentCurrency}
                        onDsInput={({ detail }) => {
                          setValues({
                            ...values,
                            paymentCurrency: detail,
                          });
                        }}
                      >
                        <div slot="value">
                          <div className="">
                            <span className="currencySymbol">
                              {logoAssets[values.paymentCurrency] ? (
                                <DsLogo
                                  name={values.paymentCurrency}
                                  width="1.3rem"
                                  height="1.3rem"
                                />
                              ) : (
                                currencySymbol(values.paymentCurrency)
                              )}
                            </span>
                            {values.paymentCurrency}
                          </div>
                        </div>
                        <div slot="options">
                          {redemption.paymentCurrencies.map((currency) => (
                            <DsSelectOption value={currency} key={currency}>
                              <div className="">
                                <span className="currencySymbol">
                                  {logoAssets[currency] ? (
                                    <DsLogo name={currency} width="1.3rem" height="1.3rem" />
                                  ) : (
                                    currencySymbol(currency)
                                  )}
                                </span>
                                {currency}
                              </div>
                            </DsSelectOption>
                          ))}
                        </div>
                      </DsSelect>
                    ) : (
                      ''
                    )}
                  </div>
                  <div className="second-section">
                    <DsInputString
                      name="bankName"
                      label={t('shared.bankName') + ' *'}
                      value={values.paymentInformation.bankName}
                      onDsInput={({ detail }) => {
                        setValues({
                          ...values,
                          paymentInformation: { ...values.paymentInformation, bankName: detail },
                        });
                      }}
                      validate={vRequired}
                    />
                    <DsInputString
                      name="accountOwner"
                      label={t('shared.accountOwner') + ' *'}
                      value={values.paymentInformation.accountOwner}
                      onDsInput={({ detail }) => {
                        setValues({
                          ...values,
                          paymentInformation: {
                            ...values.paymentInformation,
                            accountOwner: detail,
                          },
                        });
                      }}
                      validate={vRequired}
                    />
                  </div>
                  <div className="third-section">
                    <DsInputString
                      name="BicSwift"
                      label={t('shared.swiftbic') + ' *'}
                      value={values.paymentInformation.bicSwift}
                      onDsInput={({ detail }) => {
                        setValues({
                          ...values,
                          paymentInformation: { ...values.paymentInformation, bicSwift: detail },
                        });
                      }}
                      validate={vRequired}
                    />
                    <DsInputString
                      name="iban"
                      label={t('shared.iban') + ' *'}
                      value={values.paymentInformation.iban}
                      onDsInput={({ detail }) => {
                        setValues({
                          ...values,
                          paymentInformation: { ...values.paymentInformation, iban: detail },
                        });
                      }}
                      validate={vRequired}
                    />
                  </div>

                  <div className="fourth-section">
                    {redemption.warningText && (
                      <DsTag
                        class="_mtop-3"
                        label={redemption.warningText}
                        version="warning"
                        icon="warning"
                        iconSize="1rem"
                        isSquare
                      />
                    )}

                    {redemption.infoText && (
                      <DsTag
                        class="_mtop-3"
                        label={redemption.infoText}
                        version="info"
                        icon="info"
                        iconSize="1rem"
                        isSquare
                      />
                    )}
                  </div>

                  {/* <div className="fourth-section">
                  <DsInputCheckbox
                    name="lastSavedInformations"
                    label={t('shared.redemptionLastSavedInformation')}
                  />
                </div> */}
                </div>
                <div className="redemption-calculator">
                  <DsCard shadowDistance="0" padding="4">
                    <p className="label">{t('shared.youSend')}</p>
                    <p className="value">
                      {presentToken(values.tokenAmount ?? 0, token.decimals, token.symbol)}
                    </p>
                    <div className="separator">
                      <div className="transfer-icon">
                        <DsIcon name="transfer" size="1.5rem" />
                        <hr />
                      </div>
                    </div>
                    <div className="label _flex-row _flex-align-center">
                      {t('shared.youReceive')}
                      {redemption.type === 'OPEN_ENDED' && (
                        <div className="_flex-row _flex-align-center _mleft-1">
                          <span>({t('shared.estimated')})</span>
                          <DsTooltip
                            isVisibleOnHover={true}
                            text={t('shared.amountToReceiveIsEstimated')}
                          >
                            <DsIcon
                              name="info"
                              size="0.8rem"
                              color="var(--coolGrey-400)"
                              className="_mleft-2"
                            />
                          </DsTooltip>
                        </div>
                      )}
                    </div>
                    <p className="value">
                      {hasTokenPrice ? (
                        presentCurrency(
                          new BigNumber(values.tokenAmount ?? 0)
                            .multipliedBy(tokenPrice)
                            .multipliedBy(exchangeRatio),
                          values.paymentCurrency,
                        )
                      ) : (
                        <DsTooltip
                          isVisibleOnHover={true}
                          text={t('shared.amountReceivedSettledByIssuer')}
                        >
                          <DsIcon name="info" size="0.8rem" color="var(--coolGrey-400)" />
                        </DsTooltip>
                      )}
                    </p>
                    <DsButton
                      type="submit"
                      label={t('shared.createRedemptionOrder')}
                      icon="arrow-right"
                      version="primary"
                      isFullWidth
                      isLoading={isSubmitting}
                    />
                    {redemption.calendar?.cutOff && redemption.calendar?.tradeDate ? (
                      renderCalendar()
                    ) : (
                      <></>
                    )}
                    <div className="recap">
                      <div className="row">
                        <p className="_text-coolGrey-400">{t('shared.tokensToRedeem')}</p>
                        <p className="_text-coolGrey-700">
                          {presentToken(values.tokenAmount ?? 0, token.decimals, token.symbol)}
                        </p>
                      </div>
                      <div className="row">
                        <p className="_text-coolGrey-400 _flex-align-center">
                          {redemption.type === 'OPEN_ENDED' ? (
                            <>
                              <span>{t('shared.estimatedPricePerToken')}</span>
                              {!!lastValuationDate ? (
                                <DsTooltip
                                  isVisibleOnHover
                                  text={t('shared.estimatedPricePerTokenValuation', {
                                    lastValuationDate: datetimeFormat(new Date(lastValuationDate)),
                                  })}
                                  className="_mleft-2"
                                >
                                  <DsIcon name="info" size="0.8rem" color="var(--coolGrey-400)" />
                                </DsTooltip>
                              ) : null}
                            </>
                          ) : (
                            t('shared.pricePerToken')
                          )}
                        </p>

                        <p className="_text-coolGrey-700">
                          {hasTokenPrice ? (
                            presentCurrency(
                              new BigNumber(tokenPrice)
                                .multipliedBy(exchangeRatio)
                                .decimalPlaces(2, BigNumber.ROUND_DOWN),
                              values.paymentCurrency,
                            )
                          ) : (
                            <DsTooltip
                              isVisibleOnHover={true}
                              text={t('shared.amountReceivedSettledByIssuer')}
                            >
                              <DsIcon name="info" size="0.8rem" color="var(--coolGrey-400)" />
                            </DsTooltip>
                          )}
                        </p>
                      </div>
                      <hr className="_mbottom-3" />
                      <div className="row">
                        <p className="_text-coolGrey-400 _fontw-600">
                          {redemption.type === 'OPEN_ENDED'
                            ? t('shared.estimatedAmountToReceive')
                            : t('shared.amountToReceive')}{' '}
                        </p>
                        <p className="_text-coolGrey-700 _fontw-600">
                          {hasTokenPrice ? (
                            presentCurrency(
                              new BigNumber(values.tokenAmount ?? 0)
                                .multipliedBy(tokenPrice)
                                .multipliedBy(exchangeRatio)
                                .decimalPlaces(2, BigNumber.ROUND_DOWN),
                              values.paymentCurrency,
                            )
                          ) : (
                            <DsTooltip
                              isVisibleOnHover={true}
                              text={t('shared.amountReceivedSettledByIssuer')}
                            >
                              <DsIcon name="info" size="0.8rem" color="var(--coolGrey-400)" />
                            </DsTooltip>
                          )}
                        </p>
                      </div>
                    </div>
                  </DsCard>
                </div>
              </div>
            </DsForm>
          </div>
        )}
      </div>
    </div>
  );
};

export default connect((s) => s, formActions)(RedemptionView);
